import React, { useCallback, useMemo, useState } from 'react';
import { Flex, Button, Heading, Box, Divider } from '@televet/kibble-ui';
import FeedbackStep from './FeedbackStep';
import RatingStep from './RatingStep';
import ThankYouStep from './ThankYouStep';
import { useSurveySettingsContext } from 'pages/Survey/context/SurveySettingsContext';
import WildcardTextInput, {
  WildcardInputPreviewModes,
  WildcardInputQuestionTypes,
} from 'pages/Forms/FormsContent/FormsDrafting/WildcardInputs/WildcardTextInput';

enum PreviewSteps {
  Rating,
  Feedback,
  ThankYou,
}

const SurveyPreview = (): JSX.Element => {
  const { composeSettings } = useSurveySettingsContext();
  const [currentStep, setCurrentStep] = useState<PreviewSteps>(PreviewSteps.Rating);
  const [rating, setRating] = useState<number>();
  const [feedback, setFeedback] = useState<string>('');

  const PreviewContent = (): JSX.Element => {
    switch (currentStep) {
      case PreviewSteps.Feedback:
        return <FeedbackStep feedback={feedback} setFeedback={setFeedback} rating={rating ?? 10} />;
      case PreviewSteps.ThankYou:
        return <ThankYouStep rating={rating ?? 10} />;
      default:
        return <RatingStep rating={rating} setRating={setRating} />;
    }
  };

  const nextButtonDisabled = useMemo(() => {
    return (currentStep === PreviewSteps.Rating && rating === undefined) || false;
  }, [currentStep, rating]);

  const goToNextStep = useCallback(() => {
    if (currentStep === PreviewSteps.ThankYou) return;

    setCurrentStep((current) => current + 1);
  }, [currentStep]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep === PreviewSteps.Rating) return;

    setCurrentStep((current) => current - 1);
  }, [currentStep]);

  return (
    <Flex className="preview" width={400} direction="column" p="20px 0" h="100%" justifyContent="space-between">
      <Box>
        <Flex>
          <Button
            iconName="chevronLeft"
            variant="ghostNeutral"
            onClick={goToPreviousStep}
            disabled={currentStep === PreviewSteps.Rating}
          />
        </Flex>
        <Heading size="lg" my={5}>
          {composeSettings.surveyName}
        </Heading>
        <WildcardTextInput
          text={composeSettings.introMessage}
          type={WildcardInputQuestionTypes.Intro}
          previewMode={WildcardInputPreviewModes.Survey}
        />
        <Divider my={8} />
        <Box className="preview__form">
          <PreviewContent />
        </Box>
      </Box>
      <Box className="preview__form-footer" mt={5}>
        <Button onClick={goToNextStep} disabled={nextButtonDisabled} w="100%">
          Next
        </Button>
      </Box>
    </Flex>
  );
};

export default SurveyPreview;
